import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PageHeader, PaginationControl, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import Footer from '@web/src/features/footer/footer'
import GroupCard from '@web/src/features/group/group-card/group-card'
import {
  type GroupListFilterFormValues,
  groupListFilterFormValuesFromResolvedParams,
} from '@web/src/features/group/group-list-filter-types'
import GroupListSideNav from '@web/src/features/group/group-list-side-nav/group-list-side-nav'
import GroupListTopNav from '@web/src/features/group/group-list-top-nav/group-list-top-nav'
import GroupsMenu from '@web/src/features/group/groups-menu/groups-menu'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { useTranslation } from '@web/src/lib/intl/react'
import { LayoutGridIcon, LayoutListIcon, PlusIcon, UsersIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  GroupSortEnum,
  GroupsDiscoveryQuery,
  GroupsDiscoveryQueryVariables,
  GroupsDiscoveryTagsQuery,
  GroupsDiscoveryTagsQueryVariables,
} from 'types/graphql'
import { z } from 'zod'
import { groupListPageHeadFromLoaderData } from './list.seo'

const TAGS_QUERY: TypedDocumentNode<GroupsDiscoveryTagsQuery, GroupsDiscoveryTagsQueryVariables> = gql`
  query GroupsDiscoveryTagsQuery {
    groupTags {
      id
      name
    }
  }
`

const GROUPS_QUERY: TypedDocumentNode<GroupsDiscoveryQuery, GroupsDiscoveryQueryVariables> = gql`
  query GroupsDiscoveryQuery($page: Int, $filter: GroupsFilter, $sortBy: GroupSortEnum) {
    groupsPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        slug
        name
        tagLine
        visibility
        groupType
        memberCount
        createdAt
        coverNsfwRating
        coverImageUrlMedium
        coverImageOffsetX
        coverImageOffsetY
        ownerId
        myMembership {
          id
          role
        }
        tags {
          id
          name
        }
      }
      count
      perPage
    }
  }
`

type GroupListSearchInput = {
  page?: number
  name?: string
  tags?: string
  excludeTags?: string
  sortBy?: string
  myGroups?: boolean
}

function resolveGroupListSearchParams(search: GroupListSearchInput) {
  return {
    page: search.page ?? 1,
    name: search.name,
    tags: search.tags,
    excludeTags: search.excludeTags,
    sortBy: search.sortBy ?? 'NEWEST',
    myGroups: search.myGroups,
  }
}

const useFixedParams = () => {
  const search = useSearch({ strict: false })
  return useMemo(() => resolveGroupListSearchParams(search), [search])
}

export const Route = createFileRoute('/(public)/(main-layout)/group/list')({
  component: GroupSearchPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    tags: z.string().optional(),
    excludeTags: z.string().optional(),
    sortBy: z.string().optional(),
    myGroups: z.coerce.boolean().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        tags: '',
        excludeTags: '',
        sortBy: 'NEWEST',
        myGroups: false,
      }),
    ],
  },
  head: ({ loaderData, match }) => groupListPageHeadFromLoaderData(loaderData, match.search),
  loaderDeps: ({ search }) => resolveGroupListSearchParams(search),
  loader: async ({ context, deps: d }) => {
    const deps = d as ReturnType<typeof resolveGroupListSearchParams>
    const variables: GroupsDiscoveryQueryVariables = {
      page: deps.page,
      filter: {
        name: deps.name || '',
        tags: deps.tags ? deps.tags.split(',') : [],
        excludeTags: deps.excludeTags ? deps.excludeTags.split(',') : [],
        memberOf: Boolean(deps.myGroups),
      },
      sortBy: deps.sortBy as GroupSortEnum,
    }

    const [{ queryRef, queryData }, { queryRef: tagsQueryRef }] = await Promise.all([
      queryAndPreload(context, GROUPS_QUERY, { variables }),
      queryAndPreload(context, TAGS_QUERY),
    ])
    const entries = queryData?.groupsPage?.entries ?? []
    return {
      queryRef,
      tagsQueryRef,
      seoList: {
        items: entries.slice(0, 24).map((g) => ({
          name: g.name?.trim() || 'Group',
          path: `/group/${g.slug ?? g.id}`,
          imageUrl: g.coverImageUrlMedium,
        })),
        totalCount: queryData?.groupsPage?.count ?? 0,
      },
    }
  },
})

function CreateGroupButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const handleClick = () => {
    if (hasPermission('group:create')) {
      navigate({ to: '/group/create' })
    } else {
      navigate({ to: '/supporter' })
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={<Button aria-label={t('Create a new group')} onClick={handleClick} size="icon" type="button" />}
      >
        <PlusIcon className="size-4" strokeWidth={3} />
      </TooltipTrigger>
      <TooltipContent>{t('Create a new group')}</TooltipContent>
    </Tooltip>
  )
}

type GroupCardLayoutMode = 'compact' | 'regular'

function LayoutToggleButton({ layout, onToggle }: { layout: GroupCardLayoutMode; onToggle: () => void }) {
  const { t } = useTranslation()
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={layout === 'compact' ? t('Switch to stacked group cards') : t('Switch to compact group cards')}
            onClick={onToggle}
            size="icon"
            type="button"
            variant="secondary"
          />
        }
      >
        {layout !== 'compact' ? <LayoutGridIcon className="size-4" /> : <LayoutListIcon className="size-4" />}
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {layout === 'compact' ? t('Show image above details') : t('Show image beside details')}
      </TooltipContent>
    </Tooltip>
  )
}

function GroupSearchPage() {
  const { t } = useTranslation()
  const params = useFixedParams()
  const { queryRef, tagsQueryRef } = Route.useLoaderData()
  const { data, error } = useReadQuery(queryRef as QueryRef<GroupsDiscoveryQuery>)
  const { data: tagsData } = useReadQuery(tagsQueryRef as QueryRef<GroupsDiscoveryTagsQuery>)

  const search = useSearch({ strict: false })
  const searchRef = useRef(search)
  searchRef.current = search
  const navigate = useNavigate()
  const { isAuthenticated, loading: authLoading } = useAuth()
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!isAuthenticated && params.page > 2) {
      navigate({ to: '/auth/signup' } as never)
    }
  }, [authLoading, isAuthenticated, navigate, params.page])

  const isFilterLoading = filterNavigatePending && isRoutePending

  const showMyGroups = search.myGroups === true
  const [cardLayout, setCardLayout] = useState<GroupCardLayoutMode>(showMyGroups ? 'compact' : 'regular')

  useEffect(() => {
    setCardLayout(showMyGroups ? 'compact' : 'regular')
  }, [showMyGroups])

  const resolvedGroupsPage = data?.groupsPage ?? { entries: [], count: 0, perPage: 1 }
  const resolvedGroupTags = tagsData?.groupTags ?? []

  const tagsSplit = (csv?: string) => (csv ? csv.split(',') : [])

  const listFilterDefaults = useMemo(
    (): GroupListFilterFormValues =>
      groupListFilterFormValuesFromResolvedParams({
        name: params.name,
        tags: tagsSplit(params.tags),
        excludeTags: tagsSplit(params.excludeTags),
        sortBy: params.sortBy,
      }),
    [params.name, params.tags, params.excludeTags, params.sortBy]
  )

  const listFilterResetValues = useMemo(
    (): GroupListFilterFormValues =>
      groupListFilterFormValuesFromResolvedParams({
        name: undefined,
        tags: undefined,
        excludeTags: undefined,
        sortBy: undefined,
      }),
    []
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: params.sortBy },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: params.sortBy })
  }, [params.sortBy, sortFormMethods])

  const tagsJoin = (arr?: string[]) => (arr && arr.length > 0 ? arr.join(',') : undefined)

  const applyListFilters = useCallback(
    (values: GroupListFilterFormValues, options: { includeSort: boolean }) => {
      setFilterNavigatePending(true)
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          name: values.name || undefined,
          tags: tagsJoin(values.tags),
          excludeTags: tagsJoin(values.excludeTags),
          ...(options.includeSort ? { sortBy: values.sortBy || undefined } : {}),
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      if (!isAuthenticated && nextPage > 2) {
        const nextUrl = new URL(window.location.href)
        nextUrl.searchParams.set('page', String(nextPage))
        navigate({ to: '/auth/signup' } as never)
        return
      }
      navigate({ search: { ...searchRef.current, page: nextPage }, replace: true })
    },
    [isAuthenticated, navigate]
  )

  const handleReset = useCallback(() => {
    const base: Record<string, unknown> = {}
    if (showMyGroups) base.myGroups = true
    navigate({ to: '/group/list', search: base, replace: true } as never)
  }, [navigate, showMyGroups])

  const getPagePreTitle = useCallback(() => {
    return showMyGroups ? t('My') : t('Discover')
  }, [showMyGroups, t])

  const getBookmarkTitle = useCallback(() => {
    return showMyGroups ? t('My Groups') : t('Discover Groups')
  }, [showMyGroups, t])

  const getPageTitle = useCallback(() => {
    return t('Groups')
  }, [t])

  const getPageSubtitle = useCallback(() => {
    return showMyGroups
      ? t('Browse a list of swinger and lifestyle social media groups you are a member of.')
      : t('Browse a list of swinger and lifestyle social media groups available on our platform.')
  }, [showMyGroups, t])

  const getActiveTab = useCallback(() => {
    return showMyGroups ? 'myGroups' : 'discover'
  }, [showMyGroups])

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="flex w-full flex-col md:flex-row">
      <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
        <GroupListSideNav
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          isAuthenticated={isAuthenticated}
          onFiltersApply={(values) => applyListFilters(values, { includeSort: false })}
          onReset={handleReset}
          tags={resolvedGroupTags}
        />
      </aside>

      <div className="flex w-full min-w-0 flex-1 flex-col">
        <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
        <div className="w-full max-w-full px-4 py-2 md:px-8 lg:px-16">
          <PageHeader
            breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: getBookmarkTitle() }]}
            className="mb-0 items-stretch pb-4 md:mb-4"
            highlightTitle={getPageTitle()}
            preTitle={getPagePreTitle()}
            rightChild={
              <>
                <div className="flex flex-col items-end gap-3 md:hidden">
                  {isAuthenticated && showMyGroups && <CreateGroupButton />}
                </div>
                <div className="hidden h-full flex-col items-end gap-3 md:flex">
                  {isAuthenticated ? (
                    <div className="flex gap-2">
                      <CreateGroupButton />
                    </div>
                  ) : null}

                  <div className="mt-auto">
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 text-nowrap font-medium text-muted-foreground text-sm">
                        {t('Sort By:')}
                      </span>
                      <Form formMethods={sortFormMethods} suppressHydrationWarning>
                        <NativeSelectFormField name="sortBy" onValueChange={handleSortChange}>
                          <option value="NEWEST">{t('Newest')}</option>
                          <option value="OLDEST">{t('Oldest')}</option>
                          <option value="NAME">{t('Name')}</option>
                          <option value="MEMBER_COUNT">{t('Member Count')}</option>
                          <option value="ACTIVITY">{t('Activity')}</option>
                        </NativeSelectFormField>
                      </Form>
                      <LayoutToggleButton
                        layout={cardLayout}
                        onToggle={() => setCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                      />
                    </div>
                  </div>
                </div>
              </>
            }
            subtitle={getPageSubtitle()}
          />

          <div className="mb-6 w-full md:hidden">
            <div className="mt-4">
              <GroupListTopNav
                filterDefaults={listFilterDefaults}
                filterResetValues={listFilterResetValues}
                isAuthenticated={isAuthenticated}
                onFiltersApply={(values) => applyListFilters(values, { includeSort: true })}
                onReset={handleReset}
                rightBarChildren={
                  <>
                    {isAuthenticated && showMyGroups && <CreateGroupButton />}
                    <LayoutToggleButton
                      layout={cardLayout}
                      onToggle={() => setCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                    />
                  </>
                }
                tags={resolvedGroupTags}
              />
            </div>
          </div>

          {isAuthenticated && (
            <div className="mb-8">
              <GroupsMenu activeTab={getActiveTab()} listRoute="/group/list" />
            </div>
          )}

          {resolvedGroupsPage.entries.length > 0 ? (
            <div className="flex w-full flex-col gap-0">
              <div
                className={
                  cardLayout === 'compact'
                    ? 'grid w-full grid-cols-1 items-stretch gap-4 md:gap-6'
                    : 'grid w-full grid-cols-1 items-stretch gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4'
                }
              >
                {resolvedGroupsPage.entries.map((group) => (
                  <GroupCard cardLayout={cardLayout} group={group} key={group.id} />
                ))}
              </div>

              <PaginationControl
                count={resolvedGroupsPage.count ? resolvedGroupsPage.count : 1}
                onPageChange={(e) => handlePageChange(e.page)}
                page={params.page}
                pageSize={resolvedGroupsPage.perPage ? resolvedGroupsPage.perPage : 1}
              />
            </div>
          ) : (
            <Empty className="w-full pb-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <UsersIcon className="size-8" />
                </EmptyMedia>
                <EmptyTitle>{t('No groups found')}</EmptyTitle>
                <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>

        <Footer />
      </div>
    </div>
  )
}
